import {
  ArrowLeftOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  AnagraficaDelegatoDTO,
  getGetFrontAnagraficheDelegatiQueryKey,
} from '@framework/api-client';
import { TitleSubmissionRow } from '@framework/common-web';
import { useSubmission } from '@framework/frontoffice-submission';
import { useTranslation } from '@framework/i18n';
import {
  AdvancedSearch,
  BrandedPath,
  ButtonDeleteConfirm,
  Drawer,
  FilterButton,
  FrontFilterPanel,
  FrontMessageFilteredList,
  LinkButton,
  ResponsiveCard,
  TableActionsCellSpace,
  TableData,
  generatePath,
  getNetworkErrorMessage,
  useBreadcrumbItem,
  useHistory,
  useIdFromUrl,
} from '@framework/ui';
import { Button, Divider, Space, Tooltip, message } from 'antd';
import React, { useState } from 'react';
import { RouteComponentProps } from 'react-router';
import { down } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import { queryClient } from '../../core/network/queryClient';
import { FilterDrawerButton } from '../../ui/drawer/FilterDrawerButton';
import { PageLayout } from '../../ui/layout/PageLayout';
import { NetworkAlertPage } from '../errors/network-alert/NetworkAlertPage';
import { AddressBookFilterSchema } from './AddressBookFilterSchema';
import {
  addressBookColumns,
  mobileAddressBookColumns,
} from './addressBookColumns';
import { AddressBookSearchableFields } from './addressBookSearchableFields';
import { AddressBookRecordForm } from './form/AddressBookRecordForm';
import { useAddressBook } from './useAddressBook';

interface Params {
  id: string;
}

export interface IAddressBookPageProps extends RouteComponentProps<Params> {}

/**
 * Pagina "Rubrica", contenenente la lista delle anagrafiche delegati
 */
export function AddressBookPage(props: IAddressBookPageProps) {
  const [submissionId] = useIdFromUrl(props, 'id');
  const history = useHistory();
  const { title } = useSubmission(submissionId);

  // Pannello Filtro (Desktop)
  const [openSearchPanel, setOpenSearchPanel] = useState(false);
  // Filtro Mobile (Drawer)
  const [openFilterDrawer, setOpenFilterDrawer] = useState(false);

  const isMobile = useBreakpoint(down('sm'));

  const [selected, setSelected] = useState<AnagraficaDelegatoDTO | null>(null);

  const { t } = useTranslation();

  useBreadcrumbItem({
    path: '/richieste',
    title: t('address-book.page.breadcrumb.richieste', 'Richieste'),
  });
  useBreadcrumbItem({
    path: generatePath('/richieste/:id', { id: submissionId ?? '<unknown>' }),
    title: title,
  });
  useBreadcrumbItem({
    path: generatePath('/richieste/:id/deleghe', {
      id: submissionId ?? '<unknown>',
    }),
    title: t(
      'address-book.page.breadcrumb.gestione-deleghe',
      'Gestione Deleghe',
    ),
  });
  useBreadcrumbItem({
    path: props.location.pathname as BrandedPath,
    title: t('address-book.page.breadcrumb.rubrica', 'Rubrica'),
  });

  const {
    defaultQuery,
    query,
    setQuery,
    records,
    meta,
    isLoading,
    isFetching,
    isFiltered,
    error,
    deleteMutation,
    onPaginationChange,
  } = useAddressBook();

  const filterform = (
    <AdvancedSearch
      id="submission-list"
      schema={AddressBookFilterSchema}
      fields={AddressBookSearchableFields}
      query={query}
      setQuery={setQuery}
      defaultQuery={defaultQuery}
      pageKey="pagina"
      defaultFieldName="primarySearchField"
    />
  );

  if (error) {
    return (
      <NetworkAlertPage
        message={t(
          'address-book.page.error.impossibile-caricare-la-rubrica',
          'Impossibile caricare la rubrica',
        )}
        error={error}
      />
    );
  }

  return (
    <PageLayout
      extraButtons={
        isMobile && (
          <FilterDrawerButton
            open={openFilterDrawer}
            onOpen={() => setOpenFilterDrawer(true)}
            onClose={() => setOpenFilterDrawer(false)}
          >
            {filterform}
          </FilterDrawerButton>
        )
      }
    >
      <ResponsiveCard
        noPadding
        title={
          !isMobile && (
            <TitleSubmissionRow
              right={
                <Space>
                  <LinkButton
                    icon={<ArrowLeftOutlined aria-hidden />}
                    to={generatePath('/richieste/:id/deleghe', {
                      id: submissionId ?? '<unknown>',
                    })}
                  >
                    {t('address-book.torna-alle-deleghe', 'Torna alle deleghe')}
                  </LinkButton>
                  <Button
                    type="primary"
                    onClick={() => setSelected({})}
                    icon={<PlusOutlined aria-hidden="true" />}
                  >
                    {t('address-book.create.button-label', 'Nuovo delegato')}
                  </Button>
                  <FilterButton
                    opened={openSearchPanel}
                    id="filter-button-address-book"
                    onClick={() => setOpenSearchPanel(!openSearchPanel)}
                  >
                    {t('addess-book.search', 'Ricerca')}
                  </FilterButton>
                </Space>
              }
            >
              {t('address-book.title', 'Rubrica delegati')}
            </TitleSubmissionRow>
          )
        }
      >
        <Divider style={{ margin: 0 }} />

        <FrontFilterPanel
          id="filter-panel-address-book"
          buttonId="filter-button-address-book"
          open={openSearchPanel}
        >
          {filterform}
        </FrontFilterPanel>

        <FrontMessageFilteredList
          visible={isFiltered}
          items={meta?.totale ?? null}
          message={t(
            'address-book.filtered.message',
            'Anagrafiche trovate con i filtri selezionati',
          )}
          action={
            <Button
              size="small"
              onClick={() => setQuery(defaultQuery)}
              children={t(
                'address-book.remove-filter-button.label',
                'Rimuovi filtri',
              )}
            />
          }
        />

        <TableData
          showSorterTooltip={false}
          size="small"
          dataSource={records}
          loading={isLoading || isFetching}
          pagination={{
            current: Number(query.pagina ?? 0) + 1,
            pageSize: query.limite != null ? Number(query.limite) : 10,
            total: meta?.totale ?? 0,
            pageSizeOptions: [5, 10, 20, 50],
          }}
          onChange={onPaginationChange}
          columns={[
            ...(isMobile ? mobileAddressBookColumns : addressBookColumns),
            {
              title: t('address-book.azioni', 'Azioni'),
              dataIndex: 'actions',
              width: 100,
              align: 'right',
              render: (_, record) => (
                <TableActionsCellSpace>
                  <Tooltip
                    title={t('address-book.edit-button.tooltip', 'Modifica')}
                  >
                    <Button
                      type="link"
                      icon={<EditOutlined aria-hidden="true" />}
                      aria-label={t(
                        'address-book.edit-button.aria-label',
                        'Vai alla Modifica',
                      )}
                      onClick={() => {
                        setSelected(record);
                      }}
                    />
                  </Tooltip>

                  {record.id && (
                    <ButtonDeleteConfirm
                      iconOnlyButton
                      title={t(
                        'addess-book.delete-row.confirm-title',
                        'Rimuovere la voce dalla rubrica?',
                      )}
                      buttonType="link"
                      buttonDanger
                      size="large"
                      onConfirm={async () => {
                        try {
                          await deleteMutation.mutateAsync({
                            id: record.id ?? '<unknown>',
                          });
                          await queryClient.invalidateQueries(
                            getGetFrontAnagraficheDelegatiQueryKey(),
                          );
                          message.success(t('address-book.remove.success', 'Voce rimossa con successo')); // prettier-ignore
                        } catch (e) {
                          console.error(e);
                          message.error( getNetworkErrorMessage(e, t('address-book.remove.error', "Errore durante l'eliminazione"))); // prettier-ignore
                        }
                      }}
                    />
                  )}
                </TableActionsCellSpace>
              ),
            },
          ]}
        />
        <Drawer
          open={!!selected}
          destroyOnClose
          onClose={() => {
            setSelected(null);
          }}
          maskClosable={false}
          title={t('address-book.drawer.title', 'Configurazione delegato')}
          size="large"
          placement="right"
        >
          <AddressBookRecordForm
            record={selected}
            onClose={() => {
              setSelected(null);
            }}
          />
        </Drawer>
      </ResponsiveCard>
    </PageLayout>
  );
}
